/** @jsxImportSource @emotion/react */
import { ReactNode } from 'react';
import { useTheme } from '../../ThemeProvider';
import { createStyles } from '../../utils';
import { Icon } from '../Icon';
import { Logo } from '../Logo';
import { Typography } from '../Typography';

export interface Props {
  tabs: ReactNode;
  logoutUser?: () => void;
  loggedInUser?: string;
  merchantName?: string;
}

export const Sidebar = ({ tabs, logoutUser, loggedInUser, merchantName }: Props) => {
  const styles = useStyles();
  const { palette, spacing } = useTheme();

  return (
    <aside css={styles.sidebar}>
      <div css={styles.logo}>
        <Logo width="90px" />
      </div>
      <div css={styles.merchantName}>{merchantName}</div>
      <div css={styles.tabList}>{tabs}</div>
      {loggedInUser && (
        <div
          css={[styles.profile, logoutUser && { cursor: 'pointer' }]}
          onClick={logoutUser}
          tabIndex={0}
        >
          <div>
            <Typography variant="captionSmallMedium" color={palette.grey.x500}>
              Logged in as:
            </Typography>
            <div css={styles.emailContainer}>
              <Typography
                variant="captionSmallMedium"
                color={palette.grey.x900}
              >
                {loggedInUser}
              </Typography>
            </div>
          </div>
          {logoutUser && <Icon name="logout" size={spacing.xxs} />}
        </div>
      )}
    </aside>
  );
};

const useStyles = createStyles(({ spacing, palette, shape, breakpoints }) => ({
  sidebar: {
    height: spacing.l,
    background: palette.white,
    border: `1px solid ${palette.grey.x100}`,
    borderBottom: 'none',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    [breakpoints.tabletMedium]: {
      height: '100%',
      borderRadius: shape.borderRadius,
      padding: `${spacing.xxs} ${spacing.xxxs} ${spacing.xxs} ${spacing.xxxs}`,
      alignItems: 'start',
      justifyContent: 'start',
      minWidth: `calc(${spacing.xxxl} - ${spacing.s})`,
      width: '100%',
      borderBottom: `1px solid ${palette.grey.x100}`,
    },
  },
  logo: {
    display: 'none',
    [breakpoints.tabletMedium]: {
      display: 'block',
      padding: `${spacing.xxxs} ${spacing.xxs} 0 ${spacing.xxs}`,
    },
  },
  merchantName: {
    display: 'block',
    marginLeft: spacing.xxs,
  },
  tabList: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xxxs,
    width: '100%',
    justifyContent: 'space-evenly',
    [breakpoints.tabletMedium]: {
      marginTop: spacing.s,
      display: 'block',
      gap: 0,
    },
    overflowY: 'scroll',
  },
  profile: {
    display: 'none',
    [breakpoints.tabletMedium]: {
      background: palette.grey.x50,
      borderRadius: shape.borderRadius,
      padding: spacing.xxxs,
      gridTemplateColumns: '1fr auto',
      alignItems: 'center',
      width: '100%',
      display: 'grid',
    },
  },
  emailContainer: {
    maxWidth: `calc(${spacing.xxxl} - ${spacing.xl})`,
    '> p': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
}));
